// Track 5 dashboard — replaces the Track 0 stub. Composed of:
//   * Stale Tiller banner   (Track 3 endpoint; 501-tolerant)
//   * Manual Entry Pending  (Track 3 endpoint; 501-tolerant)
//   * Balance Totals card   (this track)
//   * Balances by Customer  (this track)
//   * Missing Updates       (Track 3 endpoint; 501-tolerant)
//   * Notifications feed    (Track 6 endpoint; 501-tolerant)
//   * Large Transactions    (this track)

import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError, apiFetch, dashboard } from '@/lib/api';
import { formatBalance, formatTransaction } from '@/lib/format';
import type { IntegratedWithoutTillerRow, StaleTillerRow } from '@frt/shared-types';

const BANNER_PREFERRED_KEY = 'frt-dashboard-stale-window-days';
const DEFAULT_STALE_WINDOW_DAYS = 3;

// Use centralized formatters from @/lib/format. Balance totals use
// formatBalance (rounded to whole dollars, no cents); the Large Transactions
// widget uses formatTransaction (exact, two decimals).
const fmtCurrency = formatBalance;

function todayMinus(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function isPlaceholder(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 501;
}

export function DashboardPage() {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
      <StaleTillerBanner />
      <IntegratedWithoutTillerBanner />
      <ManualPendingBanner />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BalanceTotalsCard />
        <BalancesByCustomerCard />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MissingUpdatesCard />
        <NotificationsCard />
      </div>
      <LargeTransactionsCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

interface StaleTillerAlert {
  data: StaleTillerRow[];
  threshold_days: number;
}

function StaleTillerBanner() {
  const q = useQuery<StaleTillerAlert | null>({
    queryKey: ['alerts', 'stale-tiller'],
    queryFn: async () => {
      try {
        return await apiFetch<StaleTillerAlert>('/alerts/stale-tiller');
      } catch (err) {
        if (isPlaceholder(err)) return null;
        throw err;
      }
    },
    retry: false,
  });
  if (q.isLoading) return null;
  if (!q.data) {
    return null; // either no data or Track 3 not merged — silent
  }
  if (q.data.data.length === 0) return null;
  const ids = q.data.data.map((r) => r.account_id).join(',');
  return (
    <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <strong className="font-semibold">Stale Tiller —</strong> {q.data.data.length} integrated
      account{q.data.data.length === 1 ? '' : 's'} past the staleness threshold.{' '}
      <Link to="/accounts" search={{ ids }} className="underline">
        Review
      </Link>
    </div>
  );
}

// Data-integrity check: any active account with tiller_status='integrated'
// must have BOTH tiller_account_id and tiller_account_reference. Otherwise
// the Mapping View / sync flow has nothing to anchor balances to.
interface IntegratedWithoutTillerAlert {
  data: IntegratedWithoutTillerRow[];
}

function IntegratedWithoutTillerBanner() {
  const q = useQuery<IntegratedWithoutTillerAlert | null>({
    queryKey: ['alerts', 'integrated-without-tiller'],
    queryFn: async () => {
      try {
        return await apiFetch<IntegratedWithoutTillerAlert>(
          '/alerts/integrated-without-tiller',
        );
      } catch (err) {
        if (isPlaceholder(err)) return null;
        throw err;
      }
    },
    retry: false,
  });
  if (q.isLoading || !q.data || q.data.data.length === 0) return null;
  const ids = q.data.data.map((r) => r.account_id).join(',');
  return (
    <div className="rounded border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
      <strong className="font-semibold">Integrated accounts missing Tiller link —</strong>{' '}
      {q.data.data.length} account{q.data.data.length === 1 ? '' : 's'} marked as
      integrated lack a Tiller Account ID or Reference.{' '}
      <Link to="/accounts" search={{ ids }} className="underline">
        Review
      </Link>
    </div>
  );
}

interface ManualPendingAlert {
  period: string;
  data: Array<{ account_id: number }>;
}

function ManualPendingBanner() {
  const period = currentMonth();
  const q = useQuery<ManualPendingAlert | null>({
    queryKey: ['alerts', 'manual-pending', period],
    queryFn: async () => {
      try {
        return await apiFetch<ManualPendingAlert>(`/alerts/manual-pending/${period}`);
      } catch (err) {
        if (isPlaceholder(err)) return null;
        throw err;
      }
    },
    retry: false,
  });
  if (q.isLoading || !q.data || q.data.data.length === 0) return null;
  return (
    <div className="rounded border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
      <strong className="font-semibold">Manual entry pending —</strong> {q.data.data.length} account
      {q.data.data.length === 1 ? '' : 's'} in the manual pool still need a balance for{' '}
      <code className="rounded bg-rose-100 px-1">{q.data.period}</code>.{' '}
      <Link to="/manual-entry" className="underline">
        Open manual entry
      </Link>
    </div>
  );
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Balance totals
// ---------------------------------------------------------------------------

function BalanceTotalsCard() {
  const q = useQuery({
    queryKey: ['dashboard', 'balance-totals'],
    queryFn: () => dashboard.balanceTotals(),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Balance Totals</CardTitle>
      </CardHeader>
      <CardBody>
        {q.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : q.error ? (
          <div className="text-sm text-red-600">{(q.error as Error).message}</div>
        ) : q.data ? (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-1 pl-1 text-left">Source</th>
                <th className="py-1 text-right">Current</th>
                <th className="py-1 pr-1 text-right">
                  Last Month-End
                  {q.data.last_month_end_period && (
                    <span className="ml-1 text-slate-400 normal-case">
                      ({q.data.last_month_end_period})
                    </span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="py-1 pl-1 font-medium text-slate-700">All</td>
                <td className="py-1 text-right tabular-nums">{fmtCurrency(q.data.current.all)}</td>
                <td className="py-1 pr-1 text-right tabular-nums">
                  {fmtCurrency(q.data.last_month_end.all)}
                </td>
              </tr>
              <tr>
                <td className="py-1 pl-1 text-slate-600">Tiller</td>
                <td className="py-1 text-right tabular-nums">{fmtCurrency(q.data.current.tiller)}</td>
                <td className="py-1 pr-1 text-right tabular-nums">
                  {fmtCurrency(q.data.last_month_end.tiller)}
                </td>
              </tr>
              <tr>
                <td className="py-1 pl-1 text-slate-600">Manual</td>
                <td className="py-1 text-right tabular-nums">{fmtCurrency(q.data.current.manual)}</td>
                <td className="py-1 pr-1 text-right tabular-nums">
                  {fmtCurrency(q.data.last_month_end.manual)}
                </td>
              </tr>
            </tbody>
          </table>
        ) : null}
      </CardBody>
    </Card>
  );
}

function BalancesByCustomerCard() {
  const q = useQuery({
    queryKey: ['dashboard', 'balance-totals', 'by-customer'],
    queryFn: () => dashboard.balanceTotalsByCustomer(),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Balances by FRT Customer</CardTitle>
      </CardHeader>
      <CardBody className="overflow-auto">
        {q.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : q.error ? (
          <div className="text-sm text-red-600">{(q.error as Error).message}</div>
        ) : q.data ? (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-1 pl-1 text-left">Customer</th>
                <th className="py-1 text-left">Code</th>
                <th className="py-1 text-right">Current</th>
                <th className="py-1 text-right">Last Month-End</th>
                <th className="py-1 text-right">Δ</th>
                <th className="py-1 pr-1 text-right"># Accts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.rows.map((r) => (
                <tr key={`${r.frt_customer_code}-${r.frt_customer_id}`}>
                  <td className="py-1 pl-1">{r.frt_customer_name}</td>
                  <td className="py-1 text-slate-500">{r.frt_customer_code}</td>
                  <td className="py-1 text-right tabular-nums">{fmtCurrency(r.current)}</td>
                  <td className="py-1 text-right tabular-nums">{fmtCurrency(r.last_month_end)}</td>
                  <td
                    className={`py-1 text-right tabular-nums ${
                      r.delta > 0 ? 'text-emerald-700' : r.delta < 0 ? 'text-red-700' : 'text-slate-500'
                    }`}
                  >
                    {fmtCurrency(r.delta)}
                  </td>
                  <td className="py-1 pr-1 text-right tabular-nums">{r.account_count}</td>
                </tr>
              ))}
              <tr className="font-medium border-t border-slate-300">
                <td className="py-1 pl-1">Total</td>
                <td />
                <td className="py-1 text-right tabular-nums">{fmtCurrency(q.data.totals.current)}</td>
                <td className="py-1 text-right tabular-nums">
                  {fmtCurrency(q.data.totals.last_month_end)}
                </td>
                <td className="py-1 text-right tabular-nums">{fmtCurrency(q.data.totals.delta)}</td>
                <td className="py-1 pr-1 text-right tabular-nums">{q.data.totals.account_count}</td>
              </tr>
            </tbody>
          </table>
        ) : null}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Missing updates widget
// ---------------------------------------------------------------------------

interface StaleUpdatesResponse {
  data: Array<{
    account_id: number;
    display_name: string;
    last_balance_at: string | null;
    source: string | null;
    days_stale: number;
  }>;
  hide_within_days: number;
}

function MissingUpdatesCard() {
  const [days, setDays] = useState<number>(() => {
    if (typeof localStorage === 'undefined') return DEFAULT_STALE_WINDOW_DAYS;
    const v = localStorage.getItem(BANNER_PREFERRED_KEY);
    return v ? Math.max(0, Number(v) || DEFAULT_STALE_WINDOW_DAYS) : DEFAULT_STALE_WINDOW_DAYS;
  });
  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(BANNER_PREFERRED_KEY, String(days));
    }
  }, [days]);
  const q = useQuery<StaleUpdatesResponse | null>({
    queryKey: ['alerts', 'stale-updates', days],
    queryFn: async () => {
      try {
        return await apiFetch<StaleUpdatesResponse>(
          `/alerts/stale-updates?hide_within_days=${days}`,
        );
      } catch (err) {
        if (isPlaceholder(err)) return null;
        throw err;
      }
    },
    retry: false,
  });
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Missing Updates</CardTitle>
          <label className="text-xs text-slate-500">
            Hide accounts updated within last
            <input
              type="number"
              min={0}
              value={days}
              onChange={(e) => setDays(Math.max(0, Number(e.target.value) || 0))}
              className="ml-2 mr-1 w-12 rounded border border-slate-300 px-1 py-0.5 text-right text-xs"
            />
            days
          </label>
        </div>
      </CardHeader>
      <CardBody className="overflow-auto">
        {q.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : !q.data ? (
          <NeedsTrackBadge track={3}>
            Stale-update detection is owned by Track 3.
          </NeedsTrackBadge>
        ) : q.data.data.length === 0 ? (
          <div className="text-sm text-slate-500">No accounts past the threshold.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-1 pl-1 text-left">Account</th>
                <th className="py-1 text-left">Last balance</th>
                <th className="py-1 text-left">Source</th>
                <th className="py-1 pr-1 text-right">Days stale</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.data.slice(0, 25).map((r) => (
                <tr key={r.account_id}>
                  <td className="py-1 pl-1">
                    <Link to="/accounts/$id" params={{ id: String(r.account_id) }}>
                      {r.display_name}
                    </Link>
                  </td>
                  <td className="py-1 text-slate-600">{r.last_balance_at ?? '—'}</td>
                  <td className="py-1 text-slate-500">{r.source ?? '—'}</td>
                  <td className="py-1 pr-1 text-right tabular-nums">{r.days_stale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Notifications feed
// ---------------------------------------------------------------------------

interface NotificationListItem {
  id: number;
  kind: string;
  payload: Record<string, unknown> | null;
  created_at: string;
  read_at: string | null;
}

function NotificationsCard() {
  const q = useQuery<{ data: NotificationListItem[] } | null>({
    queryKey: ['notifications', 'feed'],
    queryFn: async () => {
      try {
        return await apiFetch<{ data: NotificationListItem[] }>('/notifications?per_page=10');
      } catch (err) {
        if (isPlaceholder(err)) return null;
        throw err;
      }
    },
    retry: false,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
      </CardHeader>
      <CardBody>
        {q.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : !q.data ? (
          <NeedsTrackBadge track={6}>Notifications inbox is owned by Track 6.</NeedsTrackBadge>
        ) : q.data.data.length === 0 ? (
          <div className="text-sm text-slate-500">No recent notifications.</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {q.data.data.map((n) => (
              <li
                key={n.id}
                className={n.read_at ? 'text-slate-500' : 'text-slate-900 font-medium'}
              >
                <span className="mr-2 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                  {n.kind}
                </span>
                <span className="text-xs text-slate-400">
                  {new Date(n.created_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Large transactions
// ---------------------------------------------------------------------------

type LargeTxSortField =
  | 'posted_date'
  | 'account_display_name'
  | 'amount'
  | 'description'
  | 'category';

function LargeTransactionsCard() {
  // 30-day rolling window. The API caps at 500 rows; 200 is plenty for the
  // dashboard widget and lets client-side sort see the whole window without
  // a follow-up fetch.
  const q = useQuery({
    queryKey: ['dashboard', 'large-transactions', 'last-30-days'],
    queryFn: () => dashboard.largeTransactions({ since: todayMinus(30), limit: 200 }),
  });

  const [sortField, setSortField] = useState<LargeTxSortField>('posted_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    if (!q.data) return [];
    const rows = [...q.data.data];
    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      // null/undefined sorts last regardless of direction so blanks bunch.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (sortField === 'amount') return ((av as number) - (bv as number)) * dir;
      // strings (posted_date is ISO YYYY-MM-DD which sorts lexicographically)
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [q.data, sortField, sortDir]);

  const onSort = (field: LargeTxSortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      // Numeric/date columns default to descending (largest/newest first);
      // text columns default to ascending (A→Z).
      setSortDir(field === 'amount' || field === 'posted_date' ? 'desc' : 'asc');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Large Transactions</CardTitle>
          <span className="text-xs text-slate-500">last 30 days</span>
        </div>
      </CardHeader>
      <CardBody className="overflow-auto">
        {q.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : q.error ? (
          <div className="text-sm text-red-600">{(q.error as Error).message}</div>
        ) : q.data && q.data.data.length === 0 ? (
          <div className="text-sm text-slate-500">
            No transactions ≥ {fmtCurrency(q.data.threshold)} in this window.
          </div>
        ) : q.data ? (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <SortableTh
                  field="posted_date"
                  label="Posted"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={onSort}
                  className="pl-1 text-left"
                />
                <SortableTh
                  field="account_display_name"
                  label="Account"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={onSort}
                  className="text-left"
                />
                <SortableTh
                  field="amount"
                  label="Amount"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={onSort}
                  className="text-right"
                />
                <SortableTh
                  field="description"
                  label="Description"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={onSort}
                  className="text-left"
                />
                <SortableTh
                  field="category"
                  label="Category"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={onSort}
                  className="pr-1 text-left"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((tx) => (
                <tr key={tx.id}>
                  <td className="py-1 pl-1 text-slate-600">{tx.posted_date}</td>
                  <td className="py-1">
                    <Link to="/accounts/$id" params={{ id: String(tx.account_id) }}>
                      {tx.account_display_name}
                    </Link>
                  </td>
                  <td
                    className={`py-1 text-right tabular-nums ${
                      tx.amount < 0 ? 'text-red-700' : 'text-slate-900'
                    }`}
                  >
                    {formatTransaction(tx.amount)}
                  </td>
                  <td className="py-1 text-slate-600">{tx.description ?? '—'}</td>
                  <td className="py-1 pr-1 text-slate-500">{tx.category ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </CardBody>
    </Card>
  );
}

function SortableTh({
  field,
  label,
  sortField,
  sortDir,
  onSort,
  className = '',
}: {
  field: LargeTxSortField;
  label: string;
  sortField: LargeTxSortField;
  sortDir: 'asc' | 'desc';
  onSort: (f: LargeTxSortField) => void;
  className?: string;
}) {
  const active = sortField === field;
  // Always render an arrow so every column reads as sortable; the inactive
  // columns get a faded ↕ glyph and the active column shows the direction.
  const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th className={`py-1 ${className}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`cursor-pointer select-none uppercase tracking-wide hover:text-slate-900 ${
          active ? 'text-slate-900' : ''
        }`}
      >
        {label}
        <span className={`ml-1 ${active ? 'text-slate-700' : 'text-slate-300'}`}>{arrow}</span>
      </button>
    </th>
  );
}

// ---------------------------------------------------------------------------
// shared "needs Track N" badge for endpoints that 501-out gracefully
// ---------------------------------------------------------------------------

function NeedsTrackBadge({ track, children }: { track: number; children?: React.ReactNode }) {
  return (
    <div className="text-sm text-slate-600">
      <span className="mr-2 inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        needs Track {track}
      </span>
      {children}
    </div>
  );
}
